import NotificationsApi from "../api/notifications";

const urlBase64ToUint8Array = (base64String) => {
    try {
        const sanitized = base64String.trim();
        const padding = "=".repeat((4 - (sanitized.length % 4)) % 4);
        const base64 = (sanitized + padding).replace(/-/g, "+").replace(/_/g, "/");
        const rawData = window.atob(base64);
        const result = Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
        return result;
    } catch (e) {
        console.error("VAPID key conversion failed:", e);
        return null;
    }
};

class NotificationService {
    static isSupported() {
        return "serviceWorker" in navigator && "PushManager" in window;
    }

    static async registerServiceWorker() {
        if (!this.isSupported()) return null;

        try {
            const swUrl = "/sw.js";
            console.log("Registering service worker:", swUrl);
            await navigator.serviceWorker.register(swUrl, { scope: "/" });

            // Wait for the service worker to be ready
            const registration = await navigator.serviceWorker.ready;
            console.log("Service Worker ready:", registration);
            return registration;
        } catch (error) {
            console.error("Service Worker registration failed:", error);
            return null;
        }
    }

    static async subscribeUser(registration) {
        try {
            const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;

            if (!vapidPublicKey) {
                console.error("VAPID Public Key not found. Please check your .env file or restart the dev server.");
                return null;
            }

            console.log("Starting subscription process...");

            // FORCE UNSUBSCRIBE FIRST: This is the critical fix for AbortError
            const existingSubscription = await registration.pushManager.getSubscription();
            if (existingSubscription) {
                console.log("Unsubscribing from existing subscription to avoid conflicts...");
                await existingSubscription.unsubscribe();
            }

            const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
            if (!convertedKey) {
                throw new Error("Invalid VAPID key format.");
            }

            // Small delay to ensure SW is fully ready
            await new Promise(resolve => setTimeout(resolve, 500));

            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: convertedKey
            });

            console.log("New subscription created successfully:", subscription);
            return subscription;
        } catch (error) {
            console.error("DETAILED_SUBSCRIPTION_ERROR:", {
                message: error.message,
                name: error.name,
                code: error.code,
                vapidLength: process.env.REACT_APP_VAPID_PUBLIC_KEY?.length
            });

            if (error.name === 'AbortError') {
                console.warn("Push subscription aborted. This can happen in Incognito mode or if the push service is unavailable.");
            }

            return null;
        }
    }

    static async init(token) {
        console.log("NotificationService.init called with token:", token ? "exists" : "null");
        if (!this.isSupported()) {
            console.warn("Web Push is NOT supported in this browser.");
            return;
        }

        try {
            const registration = await this.registerServiceWorker();
            if (!registration) {
                console.error("Failed to register Service Worker.");
                return;
            }

            console.log("Checking existing subscription...");
            let subscription = await registration.pushManager.getSubscription();

            if (!subscription) {
                console.log("No existing subscription. Permission status:", Notification.permission);
                if (Notification.permission === "granted") {
                    console.log("Permission granted but no subscription, subscribing now...");
                    subscription = await this.subscribeUser(registration);
                } else if (Notification.permission === "default") {
                    console.log("Permission default, requesting from user...");
                    const permission = await Notification.requestPermission();
                    console.log("User response to permission request:", permission);
                    if (permission === "granted") {
                        subscription = await this.subscribeUser(registration);
                    }
                } else {
                    console.warn("Permission is denied. User must enable it manually.");
                }
            } else {
                console.log("Existing subscription found.");
            }

            if (subscription && token) {
                console.log("Subscription and token present. Sending to backend...");
                // Wrap in the format expected by the backend
                // Note: Removed is_standalone to match backend schema exactly
                const payload = {
                    status_type: "subscribe",
                    group: "manager_group",
                    subscription: subscription.toJSON(),
                };

                try {
                    const response = await NotificationsApi.SaveWebPushSubscription(payload, token);
                    console.log("Subscription saved to backend successfully:", response.data);
                } catch (apiError) {
                    console.error("Backend failed to save subscription:", apiError.response?.data || apiError.message);
                }
            } else {
                console.log("Skipping backend save: ", { hasSubscription: !!subscription, hasToken: !!token });
            }
        } catch (error) {
            console.error("Notification initialization failed:", error);
        }
    }

    static isStandalone() {
        return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone || document.referrer.includes("android-app://");
    }

    static clearBadge() {
        if ("clearAppBadge" in navigator) {
            navigator.clearAppBadge().catch((err) => console.error("Error clearing badge:", err));
        }
    }
}

export default NotificationService;